import { Box, Button, Chip, Typography } from '@mui/material';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import CategorySection from './CategorySection';
import InventorySelection from './InventorySelection';
import useCachedCategoriesList from '@src/adapters/hooks/category/useCachedCategoriesList';
import CategoryHeaderDTO from '@src/adapters/dto/category/CategoryHeaderDTO';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';
import MigrationDetailDTO from '@src/adapters/dto/migration/MigrationDetailDTO';
import { MigrationStatus } from '@src/constants/MigrationStatus';

interface Props {
  items: MigrationItemViewDTO[];
  highlightedId?: string;
  lockView: Boolean;
  migration: MigrationDetailDTO;
  deleteItem(id: string): void;
  itemUpdated(item: MigrationItemUpdateDTO): void;
  highlightItem(suggestionId?: string): void;
  inventoryItemAdded(item: MigrationItemViewDTO): void;
  handleItemApprovalUpdate(
    itemId: string,
    suggestionId: string | undefined,
    approved: boolean
  ): void;
}

function CardItems({
  items,
  highlightedId,
  lockView,
  migration,
  deleteItem,
  itemUpdated: noteSaved,
  handleItemApprovalUpdate,
  highlightItem,
  inventoryItemAdded,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const [categories, setCategories] = useState<CategoryHeaderDTO[]>([]);
  const [collapsed, setCollapsed] = useState(true);

  const { getList } = useCachedCategoriesList();

  useEffect(() => {
    getList().then((result) => {
      const categoryIds = new Set(items.map((item) => item.category.id));
      setCategories(
        result
          .filter((c) => categoryIds.has(c.id))
          .sort((a, b) => a.order - b.order)
      );
    });
  }, [getList, items]);

  const handleCardItemsToggle = () => {
    setCollapsed((prev) => !prev);
  };

  return (
    <>
      {migration.status !== MigrationStatus.Complete && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'rgba(70, 110, 229, 0.05)',
            height: '62px',
            border: '1px solid #E6E7E9',
          }}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '200px',
              padding: '8px',
            })}>
            <CheckCircleIcon
              sx={(theme) => ({ color: theme.palette.success.main })}
            />
            <Typography variant="h6">Card items</Typography>
            <Chip
              label={`${items.filter((i) => i.approval).length}/${
                items.length
              }`}
              sx={{ background: '#fff', border: '1px solid #E6E7E9' }}
            />
          </Box>

          <Box sx={{ display: 'flex' }}>
            {expanded && (
              <ExpandLessIcon
                sx={(theme) => ({
                  marginLeft: '16px',
                  marginRight: '24px',
                  color: theme.palette.action.active,
                  cursor: 'pointer',
                })}
                onClick={() => setExpanded(false)}
              />
            )}
            {!expanded && (
              <ExpandMoreIcon
                sx={(theme) => ({
                  marginLeft: '16px',
                  marginRight: '24px',
                  color: theme.palette.action.active,
                  cursor: 'pointer',
                })}
                onClick={() => setExpanded(true)}
              />
            )}
          </Box>
        </Box>
      )}
      {expanded && migration.status !== MigrationStatus.Complete && (
        <Box sx={{ borderBottom: '1px solid  #E6E7E9' }}>
          <InventorySelection
            migrationId={migration.id}
            inventoryItemAdded={inventoryItemAdded}
          />
        </Box>
      )}
      {expanded && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'right',
            alignItems: 'right',
            width: '100%',
            padding: '8px',
            background: 'rgba(70, 110, 229, 0.05)',
            borderBottom: '1px solid #E6E7E9',
          })}>
          <Button
            color="primary"
            variant="outlined"
            size="small"
            sx={{ height: '30px' }}
            onClick={handleCardItemsToggle}>
            {collapsed ? 'Expand All' : 'Collapse all'}
          </Button>
        </Box>
      )}

      {expanded &&
        categories.map((c) => (
          <CategorySection
            items={items.filter((i) => i.category.id === c.id)}
            highlightedId={highlightedId}
            deleteItem={deleteItem}
            handleItemApprovalUpdate={handleItemApprovalUpdate}
            highlightItem={highlightItem}
            itemUpdated={noteSaved}
            key={c.id}
            lockView={lockView}
            collapsed={collapsed}
            status={migration.status}
            isOwner={migration.isOwner}
          />
        ))}
    </>
  );
}

export default CardItems;
